/**
 * Country → currency / regime / states lookup for SERP.
 * Used by Company Setup + Super Admin console for smart, cascading dropdowns.
 */

export const COUNTRIES = [
  { code: "IN", name: "India",           currency: "INR", regime: "IN_GST" },
  { code: "AE", name: "United Arab Emirates", currency: "AED", regime: "AE_VAT" },
  { code: "US", name: "United States",   currency: "USD", regime: "US_SALES_TAX" },
  { code: "GB", name: "United Kingdom",  currency: "GBP", regime: "UK_VAT" },
  { code: "DE", name: "Germany",         currency: "EUR", regime: "EU_VAT" },
  { code: "FR", name: "France",          currency: "EUR", regime: "EU_VAT" },
  { code: "IT", name: "Italy",           currency: "EUR", regime: "EU_VAT" },
  { code: "ES", name: "Spain",           currency: "EUR", regime: "EU_VAT" },
  { code: "NL", name: "Netherlands",     currency: "EUR", regime: "EU_VAT" },
  { code: "AU", name: "Australia",       currency: "AUD", regime: "NONE" },
  { code: "CA", name: "Canada",          currency: "CAD", regime: "NONE" },
  { code: "SG", name: "Singapore",       currency: "SGD", regime: "NONE" },
  { code: "OTHER", name: "Other",        currency: "USD", regime: "NONE" },
];

export const CURRENCIES = [
  { code: "INR", label: "Indian Rupee (₹)" },
  { code: "USD", label: "US Dollar ($)" },
  { code: "EUR", label: "Euro (€)" },
  { code: "GBP", label: "British Pound (£)" },
  { code: "AED", label: "UAE Dirham (د.إ)" },
  { code: "AUD", label: "Australian Dollar (A$)" },
  { code: "CAD", label: "Canadian Dollar (C$)" },
  { code: "SGD", label: "Singapore Dollar (S$)" },
  { code: "JPY", label: "Japanese Yen (¥)" },
  { code: "CNY", label: "Chinese Yuan (¥)" },
  { code: "ZAR", label: "South African Rand (R)" },
  { code: "BRL", label: "Brazilian Real (R$)" },
];

export const TAX_REGIMES = [
  { code: "IN_GST",        label: "India GST" },
  { code: "AE_VAT",        label: "UAE VAT" },
  { code: "EU_VAT",        label: "EU VAT" },
  { code: "UK_VAT",        label: "UK VAT" },
  { code: "US_SALES_TAX",  label: "US Sales Tax" },
  { code: "NONE",          label: "None / Manual" },
];

/**
 * States / regions for supported countries. `code` is the ISO/state code
 * used for GST place-of-supply etc.
 */
export const STATES = {
  IN: [
    ["AN", "Andaman & Nicobar"], ["AP", "Andhra Pradesh"], ["AR", "Arunachal Pradesh"],
    ["AS", "Assam"], ["BR", "Bihar"], ["CH", "Chandigarh"], ["CT", "Chhattisgarh"],
    ["DN", "Dadra & Nagar Haveli & Daman & Diu"], ["DL", "Delhi"], ["GA", "Goa"],
    ["GJ", "Gujarat"], ["HR", "Haryana"], ["HP", "Himachal Pradesh"],
    ["JK", "Jammu & Kashmir"], ["JH", "Jharkhand"], ["KA", "Karnataka"],
    ["KL", "Kerala"], ["LA", "Ladakh"], ["LD", "Lakshadweep"], ["MP", "Madhya Pradesh"],
    ["MH", "Maharashtra"], ["MN", "Manipur"], ["ML", "Meghalaya"], ["MZ", "Mizoram"],
    ["NL", "Nagaland"], ["OR", "Odisha"], ["PY", "Puducherry"], ["PB", "Punjab"],
    ["RJ", "Rajasthan"], ["SK", "Sikkim"], ["TN", "Tamil Nadu"], ["TG", "Telangana"],
    ["TR", "Tripura"], ["UP", "Uttar Pradesh"], ["UT", "Uttarakhand"], ["WB", "West Bengal"],
  ],
  AE: [
    ["AZ", "Abu Dhabi"], ["DU", "Dubai"], ["SH", "Sharjah"], ["AJ", "Ajman"],
    ["UQ", "Umm Al Quwain"], ["RK", "Ras Al Khaimah"], ["FU", "Fujairah"],
  ],
  US: [
    ["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"],
    ["CA", "California"], ["CO", "Colorado"], ["CT", "Connecticut"], ["DE", "Delaware"],
    ["FL", "Florida"], ["GA", "Georgia"], ["HI", "Hawaii"], ["ID", "Idaho"],
    ["IL", "Illinois"], ["IN", "Indiana"], ["IA", "Iowa"], ["KS", "Kansas"],
    ["KY", "Kentucky"], ["LA", "Louisiana"], ["ME", "Maine"], ["MD", "Maryland"],
    ["MA", "Massachusetts"], ["MI", "Michigan"], ["MN", "Minnesota"], ["MS", "Mississippi"],
    ["MO", "Missouri"], ["MT", "Montana"], ["NE", "Nebraska"], ["NV", "Nevada"],
    ["NH", "New Hampshire"], ["NJ", "New Jersey"], ["NM", "New Mexico"], ["NY", "New York"],
    ["NC", "North Carolina"], ["ND", "North Dakota"], ["OH", "Ohio"], ["OK", "Oklahoma"],
    ["OR", "Oregon"], ["PA", "Pennsylvania"], ["RI", "Rhode Island"], ["SC", "South Carolina"],
    ["SD", "South Dakota"], ["TN", "Tennessee"], ["TX", "Texas"], ["UT", "Utah"],
    ["VT", "Vermont"], ["VA", "Virginia"], ["WA", "Washington"], ["WV", "West Virginia"],
    ["WI", "Wisconsin"], ["WY", "Wyoming"], ["DC", "District of Columbia"],
  ],
  GB: [
    ["ENG", "England"], ["SCT", "Scotland"], ["WLS", "Wales"], ["NIR", "Northern Ireland"],
  ],
  DE: [
    ["BW", "Baden-Württemberg"], ["BY", "Bavaria"], ["BE", "Berlin"], ["BB", "Brandenburg"],
    ["HB", "Bremen"], ["HH", "Hamburg"], ["HE", "Hesse"], ["MV", "Mecklenburg-Vorpommern"],
    ["NI", "Lower Saxony"], ["NW", "North Rhine-Westphalia"], ["RP", "Rhineland-Palatinate"],
    ["SL", "Saarland"], ["SN", "Saxony"], ["ST", "Saxony-Anhalt"], ["SH", "Schleswig-Holstein"],
    ["TH", "Thuringia"],
  ],
  FR: [
    ["ARA", "Auvergne-Rhône-Alpes"], ["BFC", "Bourgogne-Franche-Comté"], ["BRE", "Brittany"],
    ["CVL", "Centre-Val de Loire"], ["COR", "Corsica"], ["GES", "Grand Est"],
    ["HDF", "Hauts-de-France"], ["IDF", "Île-de-France"], ["NOR", "Normandy"],
    ["NAQ", "Nouvelle-Aquitaine"], ["OCC", "Occitanie"], ["PDL", "Pays de la Loire"],
    ["PAC", "Provence-Alpes-Côte d'Azur"],
  ],
  AU: [
    ["NSW", "New South Wales"], ["VIC", "Victoria"], ["QLD", "Queensland"],
    ["WA", "Western Australia"], ["SA", "South Australia"], ["TAS", "Tasmania"],
    ["ACT", "Australian Capital Territory"], ["NT", "Northern Territory"],
  ],
  CA: [
    ["AB", "Alberta"], ["BC", "British Columbia"], ["MB", "Manitoba"], ["NB", "New Brunswick"],
    ["NL", "Newfoundland and Labrador"], ["NS", "Nova Scotia"], ["ON", "Ontario"],
    ["PE", "Prince Edward Island"], ["QC", "Quebec"], ["SK", "Saskatchewan"],
    ["NT", "Northwest Territories"], ["NU", "Nunavut"], ["YT", "Yukon"],
  ],
  SG: [
    ["01", "Central Region"], ["02", "East Region"], ["03", "North Region"],
    ["04", "North-East Region"], ["05", "West Region"],
  ],
  IT: [
    ["ABR", "Abruzzo"], ["BAS", "Basilicata"], ["CAL", "Calabria"], ["CAM", "Campania"],
    ["EMR", "Emilia-Romagna"], ["FVG", "Friuli-Venezia Giulia"], ["LAZ", "Lazio"],
    ["LIG", "Liguria"], ["LOM", "Lombardy"], ["MAR", "Marche"], ["MOL", "Molise"],
    ["PIE", "Piedmont"], ["PUG", "Apulia"], ["SAR", "Sardinia"], ["SIC", "Sicily"],
    ["TOS", "Tuscany"], ["TAA", "Trentino-Alto Adige"], ["UMB", "Umbria"],
    ["VDA", "Aosta Valley"], ["VEN", "Veneto"],
  ],
  ES: [
    ["AN", "Andalusia"], ["AR", "Aragon"], ["AS", "Asturias"], ["CB", "Cantabria"],
    ["CL", "Castile and León"], ["CM", "Castile-La Mancha"], ["CN", "Canary Islands"],
    ["CT", "Catalonia"], ["EX", "Extremadura"], ["GA", "Galicia"], ["IB", "Balearic Islands"],
    ["MC", "Murcia"], ["MD", "Madrid"], ["NC", "Navarre"], ["PV", "Basque Country"],
    ["RI", "La Rioja"], ["VC", "Valencian Community"],
  ],
  NL: [
    ["DR", "Drenthe"], ["FL", "Flevoland"], ["FR", "Friesland"], ["GE", "Gelderland"],
    ["GR", "Groningen"], ["LI", "Limburg"], ["NB", "North Brabant"], ["NH", "North Holland"],
    ["OV", "Overijssel"], ["UT", "Utrecht"], ["ZE", "Zeeland"], ["ZH", "South Holland"],
  ],
};

export function statesFor(countryCode) {
  return STATES[countryCode] || [];
}

export function countryDefaults(code) {
  return COUNTRIES.find((c) => c.code === code) || null;
}
